import GenericTable from "components/table";
import { useAuthContext, User } from "auth/AuthProvider";
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Trash } from "lucide-react";
import { useConfirmation } from "components/dialog/confirmation";
import api from "api/axios";
import toast from "react-hot-toast";
import { handleAxiosAndCaughtError } from "utils";
import TooltipHorizon from "components/tooltip";
import { CellContext } from "@tanstack/react-table";
import { PERMISSIONS } from "auth/permissions";
import { DATE_TIME_FORMAT } from "constants/date";
import dayjs from "dayjs";
import AddUsersForm from "./AddUsersForm";
import { useTranslation } from "react-i18next";
import { usageApi } from "api/usage";

type UserTableProps = {
  tableData: User[];
  getUsersWorkspace: () => Promise<void>;
};

const UserDataTable: React.FC<UserTableProps> = ({
  tableData,
  getUsersWorkspace,
}) => {
  const { t } = useTranslation();
  const [deletingRowId, setDeletingRowId] = React.useState<string | null>(null);
  const navigate = useNavigate();
  const { hasPermission } = useAuthContext();
  const { confirm } = useConfirmation();

  const permissions = useMemo(
    () => ({
      canAddUser: hasPermission(PERMISSIONS.ADD_USER),
      canDeleteUser: hasPermission(PERMISSIONS.DELETE_USER),
    }),
    [hasPermission]
  );

  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isLoadingUsage, setIsLoadingUsage] = useState(true);
  const [usageData, setUsageData] = useState<{
    total_users: number | null;
    max_users: number | null;
  } | null>(null);

  // Compute user limit state
  const isUserLimitReached = useMemo(() => {
    if (!usageData || usageData.total_users === null || usageData.max_users === null) {
      return false;
    }
    return usageData.total_users >= usageData.max_users;
  }, [usageData]);

  // Fetch usage data
  const refreshUsageData = useCallback(async () => {
    if (!permissions.canAddUser) {
      setIsLoadingUsage(false);
      return;
    }

    try {
      setIsLoadingUsage(true);
      const currentDate = new Date(Date.now()).toISOString().split("T")[0];
      const data = await usageApi.getUsageOverview({
        start_date: currentDate,
        end_date: currentDate,
      });

      setUsageData({
        total_users: data.total_users,
        max_users: data.max_users,
      });
    } catch (err) {
      console.error("Error fetching usage data:", err);
      setUsageData(null);
    } finally {
      setIsLoadingUsage(false);
    }
  }, [permissions.canAddUser]);

  useEffect(() => {
    refreshUsageData();
  }, [refreshUsageData]);

  const handleDeleteRow = async (userId: string) => {
    const confirmed = await confirm({
      title: t("workspaces.usersTable.removeTitle"),
      message: t("workspaces.usersTable.removeMessage"),
      confirmText: t("workspaces.usersTable.remove"),
      cancelText: t("common.cancel"),
      variant: "danger",
    });

    if (confirmed) {
      setDeletingRowId(userId);
      try {
        await api.delete(`/workspaces/users`, {
          data: { user_ids: [userId] },
        });
        await getUsersWorkspace();
        await refreshUsageData();
        toast.success(t("workspaces.usersTable.removedSuccess"));
        setDeletingRowId(null);
      } catch (err) {
        setDeletingRowId(null);
        handleAxiosAndCaughtError(err);
      }
    }
  };

  const columns = [
    {
      accessorKey: "email",
      header: () => <p>{t("common.email")}</p>,
      cell: (info: any) => (
        <p className="text-sm font-bold text-navy-700 dark:text-white">
          {info.getValue()}
        </p>
      ),
    },

    {
      accessorKey: "joined_at",
      header: () => <p>{t("users.joinedAt")}</p>,
      cell: (info: any) => (
        <p className="text-sm font-bold text-navy-700 dark:text-white">
          {dayjs(info.getValue()).format(DATE_TIME_FORMAT)}
        </p>
      ),
    },
    {
      accessorKey: "id",
      header: () => (
        <p className="p-4 text-left font-medium text-gray-900 dark:text-gray-100"></p>
      ),
      cell: (info: CellContext<User, string>) => (
        <div className="flex items-center gap-4">
          {permissions.canDeleteUser && !info.row.original.is_owner && (
            <TooltipHorizon
              trigger={
                <button
                  disabled={deletingRowId === info.getValue()}
                  onClick={() => handleDeleteRow(info.getValue())}
                  className="rounded-md p-2 text-red-500 transition-colors hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-50 dark:hover:bg-red-900"
                >
                  {deletingRowId === info.getValue() ? (
                    <div className="border-t-transparent h-4 w-4 animate-spin rounded-full border-2 border-red-500" />
                  ) : (
                    <Trash size={16} />
                  )}
                </button>
              }
              content={<div>{t("workspaces.usersTable.tooltipRemove")}</div>}
              placement="top"
              extra=""
            />
          )}
        </div>
      ),
      meta: {
        cellClassName: "min-w-[50px]",
        headerClassName: "min-w-[50px]",
      },
    },
  ];

  const renderRowClassName = useCallback(
    (row: any) => {
      const rowId = row.original.id;
      return deletingRowId === rowId ? "animate-pulse opacity-60" : "";
    },
    [deletingRowId]
  );

  return (
    <>
      <GenericTable
        data={tableData}
        columns={columns as any}
        searchPlaceholder={t("users.searchUsers")}
        onAdd={
          permissions.canAddUser ? () => setIsAddDialogOpen(true) : undefined
        }
        renderRowClassName={renderRowClassName}
        onRowClick={(item) => {
          navigate(`/dashboard/users/${item.id}`);
        }}
        addLabel={t("users.addUser")}
        disabledAddButton={
          permissions.canAddUser && (isUserLimitReached || isLoadingUsage)
        }
        toolTipTextForAddButton={
          permissions.canAddUser && isUserLimitReached
            ? t("users.userLimitReached")
            : undefined
        }
      />
      <AddUsersForm
        isOpen={isAddDialogOpen}
        onClose={() => setIsAddDialogOpen(false)}
        onUsersAdded={async () => {
          await getUsersWorkspace();
          await refreshUsageData();
        }}
      />
    </>
  );
};

export default UserDataTable;
